import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/api/client';
import { CameraCapture } from '@/components/CameraCapture';
import { DemoLayout } from '@/components/DemoLayout';
import { EnrollStepper } from '@/components/EnrollStepper';
import { LivenessCapture } from '@/components/LivenessCapture';
import { SelfieIntro } from '@/components/SelfieIntro';
import { getDemoConfig } from '@/lib/config';
import { readEnrollment, writeEnrollment } from '@/lib/enrollment';
import { describeLivenessError, type LivenessFailure } from '@/lib/livenessErrors';

type Phase =
  | { kind: 'intro'; starting: boolean }
  | { kind: 'checking'; livenessSessionId: string | null }
  | { kind: 'submitting'; step: 'validating' | 'indexing' }
  | { kind: 'error'; failure: LivenessFailure };

const MAX_ATTEMPTS = 3;

/**
 * Selfie com PROVA DE VIDA (AWS Rekognition via Amplify).
 *
 * Fases: preparo (dicas + botão) → câmera (detector tematizado) → envio
 * (prova de vida validada no BFF e face indexada) → próxima etapa. Qualquer
 * falha vira um cartão com a causa traduzida e o que fazer; a sessão de
 * liveness é nova a cada tentativa. Em MOCK_LIVENESS a câmera é a captura
 * simples.
 */
export function SelfiePage() {
  const navigate = useNavigate();
  const { mockLiveness, region } = getDemoConfig();
  const enrollment = readEnrollment();
  const [phase, setPhase] = useState<Phase>({ kind: 'intro', starting: false });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!enrollment) navigate('/cadastrar', { replace: true });
  }, [enrollment, navigate]);

  const fail = useCallback(
    (failure: LivenessFailure) => {
      setAttempts((n) => n + 1);
      setPhase({ kind: 'error', failure });
    },
    [],
  );

  const start = useCallback(async () => {
    if (!enrollment) return;
    if (mockLiveness) {
      setPhase({ kind: 'checking', livenessSessionId: null });
      return;
    }
    setPhase({ kind: 'intro', starting: true });
    try {
      const { livenessSessionId } = await api.enroll.livenessSession(enrollment.enrollmentId);
      setPhase({ kind: 'checking', livenessSessionId });
    } catch (err) {
      fail({
        title: 'Não foi possível iniciar',
        message: err instanceof ApiError ? err.message : 'O serviço de prova de vida não respondeu.',
        retryable: true,
      });
    }
  }, [enrollment, fail, mockLiveness]);

  const submit = useCallback(
    async (payload: { livenessSessionId?: string; base64Image?: string }) => {
      if (!enrollment) return;
      setPhase({ kind: 'submitting', step: 'validating' });
      // A validação da prova de vida e a indexação acontecem numa chamada só; o passo
      // visual troca depois de um instante para a espera não parecer travada.
      const timer = window.setTimeout(() => setPhase({ kind: 'submitting', step: 'indexing' }), 1_500);
      try {
        const result = await api.enroll.selfie({ enrollmentId: enrollment.enrollmentId, ...payload });
        writeEnrollment({ ...enrollment, selfieDone: true, indexJobId: result.indexJobId });
        navigate('/cadastrar/dados', { state: { confidence: result.confidence } });
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Não foi possível validar a selfie.';
        const code = err instanceof ApiError ? err.code : null;
        fail({
          title:
            code === 'LIVENESS_VERIFICATION_FAILED' || code === 'LIVENESS_CONFIDENCE_LOW'
              ? 'Prova de vida não aprovada'
              : code === 'FACE_INDEX_REJECTED'
                ? 'Não achamos um rosto nítido'
                : 'Não foi possível concluir',
          message,
          retryable: true,
        });
      } finally {
        window.clearTimeout(timer);
      }
    },
    [enrollment, fail, navigate],
  );

  if (!enrollment) return null;

  const exhausted = attempts >= MAX_ATTEMPTS;

  return (
    <DemoLayout backTo="/cadastrar" title="Cadastro">
      <EnrollStepper current="selfie" />

      {phase.kind === 'intro' && <SelfieIntro mock={mockLiveness} starting={phase.starting} onStart={() => void start()} />}

      {phase.kind === 'checking' && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="h2">{mockLiveness ? 'Tire a selfie' : 'Siga as instruções na câmera'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {mockLiveness
                ? 'Centralize o rosto e toque no botão.'
                : 'Aproxime o rosto quando pedir e fique parado enquanto a tela muda de cor.'}
            </Typography>
          </Box>

          {mockLiveness ? (
            <CameraCapture onCapture={(base64Image) => void submit({ base64Image })} />
          ) : (
            phase.livenessSessionId && (
              <LivenessCapture
                sessionId={phase.livenessSessionId}
                region={region}
                onAnalysisComplete={() => submit({ livenessSessionId: phase.livenessSessionId ?? undefined })}
                onError={(error) => fail(describeLivenessError(error))}
                onUserCancel={() => setPhase({ kind: 'intro', starting: false })}
              />
            )
          )}

          <Button variant="text" fullWidth onClick={() => setPhase({ kind: 'intro', starting: false })}>
            Cancelar
          </Button>
        </>
      )}

      {phase.kind === 'submitting' && (
        <Box className="status-card" role="status" aria-live="polite">
          <Box className="status-card__row status-card__row--done">
            <CheckCircleIcon />
            <span>{mockLiveness ? 'Foto capturada' : 'Prova de vida concluída'}</span>
          </Box>
          <Box className={`status-card__row ${phase.step === 'indexing' ? 'status-card__row--active' : 'status-card__row--done'}`}>
            {phase.step === 'validating' ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            <span>{mockLiveness ? 'Enviando a foto' : 'Validando com a Identila Live'}</span>
          </Box>
          <Box className={`status-card__row ${phase.step === 'indexing' ? 'status-card__row--active' : ''}`}>
            {phase.step === 'indexing' ? <CircularProgress size={20} /> : <Box className="status-card__dot" />}
            <span>Gerando sua biometria</span>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Só mais alguns segundos. Não feche esta tela.
          </Typography>
        </Box>
      )}

      {phase.kind === 'error' && (
        <>
          <Box className="result-card result-card--error" role="alert">
            <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }} />
            <Typography variant="h2">{phase.failure.title}</Typography>
            <Typography color="text.secondary">{phase.failure.message}</Typography>
            <Typography variant="body2" color="text.secondary">
              Tentativa {Math.min(attempts, MAX_ATTEMPTS)} de {MAX_ATTEMPTS}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 'auto', pb: 2 }}>
            {phase.failure.retryable && !exhausted ? (
              <Button variant="contained" fullWidth onClick={() => void start()}>
                Tentar de novo
              </Button>
            ) : (
              <Button variant="contained" fullWidth onClick={() => navigate('/')}>
                Voltar ao início
              </Button>
            )}
            <Button variant="text" fullWidth onClick={() => setPhase({ kind: 'intro', starting: false })}>
              Rever as dicas
            </Button>
          </Box>
        </>
      )}
    </DemoLayout>
  );
}
